import { useEffect, useRef } from 'react';

export interface Heightmap {
  worldName: string;
  worldSize: number;
  gridStep: number;
  cols: number;
  /** Metres above sea level, row-major over cols x cols. */
  heights: number[];
  /** Water as [firstIndex, length] runs over the same order. */
  water: [number, number][];
}

/**
 * The ground, drawn from the terrain survey.
 *
 * A dedicated Arma server ships 8x8 placeholder textures, so the platform can
 * never take a picture of a terrain (DYNAMIC_MISSIONS_LANDSCAPE.md §2.2). What
 * it can do is measure the height of every hundred metres and draw the shape
 * that comes out — which is what this is: a hillshade with the sea painted in,
 * under the map's own markers. For a terrain with no Steam preview it is the
 * difference between a blank square and a map.
 *
 * Drawn once per heightmap into a canvas at grid resolution and stretched by
 * CSS, so panning and zooming cost nothing: an 8 km terrain is 165 pixels
 * square of real data and no amount of zoom invents more.
 */
export function TerrainRelief({ map, size }: { map: Heightmap; size: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const el = canvas.current;
    if (!el || map.cols < 2 || map.heights.length < map.cols * map.cols) return;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    const cols = map.cols;
    const wet = new Uint8Array(cols * cols);
    (map.water ?? []).forEach(([from, length]) => {
      for (let i = from; i < from + length && i < wet.length; i++) wet[i] = 1;
    });

    let lowest = Infinity;
    let highest = -Infinity;
    for (const h of map.heights) {
      if (h < lowest) lowest = h;
      if (h > highest) highest = h;
    }
    const span = Math.max(1, highest - lowest);

    const image = ctx.createImageData(cols, cols);
    for (let iy = 0; iy < cols; iy++) {
      for (let ix = 0; ix < cols; ix++) {
        const i = iy * cols + ix;
        const at = i * 4;
        if (wet[i]) {
          image.data[at] = 90;
          image.data[at + 1] = 120;
          image.data[at + 2] = 150;
          image.data[at + 3] = 235;
          continue;
        }
        // A hillshade from the north-west, which is what a map reads as relief,
        // over a height ramp so a plateau and a valley floor are not the same grey.
        const west = map.heights[iy * cols + Math.max(0, ix - 1)];
        const north = map.heights[Math.max(0, iy - 1) * cols + ix];
        const here = map.heights[i];
        const slope = (here - west + (here - north)) / 2;
        const ramp = (here - lowest) / span;
        const base = 96 + ramp * 96;
        const shade = Math.max(0, Math.min(255, base - slope * 6));
        image.data[at] = shade;
        image.data[at + 1] = shade * 0.98;
        image.data[at + 2] = shade * 0.9;
        image.data[at + 3] = 235;
      }
    }
    el.width = cols;
    el.height = cols;
    ctx.putImageData(image, 0, 0);
  }, [map]);

  return (
    <canvas
      ref={canvas}
      className="map-relief"
      style={{
        position: 'absolute',
        inset: 0,
        width: size,
        height: size,
        imageRendering: 'auto',
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
}
